import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { UserCheckIcon, HomeIcon, ScaleIcon, CheckCircle2Icon, CalendarIcon, UsersIcon, BookOpenIcon, MapIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
const courses = [{
  icon: MapIcon,
  title: 'Living in non-Muslim countries',
  body: 'A practical roadmap for relocating and thriving in non-Muslim countries — country choice, legal essentials, housing, faith life, and raising confident Muslim children.',
  to: '#living-in-non-muslim'
}, {
  icon: UserCheckIcon,
  title: 'Identity & Values',
  body: 'Build a resilient personal identity by embodying Islamic ethical values into clear, confident decisions for modern life.',
  to: '#muslim-life'
}, {
  icon: HomeIcon,
  title: 'Family Life',
  body: 'Strengthen marriage, parenting, and family dynamics — adapted to non-Muslim societies.',
  to: '#family-marriage'
}, {
  icon: ScaleIcon,
  title: 'Professional Ethics',
  body: 'Apply Islamic principles to workplace decisions, leadership, and career growth.',
  to: '#professional-business'
}];
const courseFeatures = [{
  icon: CalendarIcon,
  title: '8 week cohorts',
  body: 'Focused, time-bounded format that respects busy adult schedules.'
}, {
  icon: UsersIcon,
  title: 'Focused group learning',
  body: 'Discussion-based cohorts with peers and experienced facilitators.'
}, {
  icon: BookOpenIcon,
  title: 'Applied learning',
  body: 'Practical, contextual content — not theory. Every session leads to action.'
}];
export function CoreCourses() {
  return <>
      <SubpageHero eyebrow="Practical guidance for everyday decisions in 8 week cohorts" title="Core Courses" paragraphs={['Our most applied courses for Muslim adults living in non-Muslim countries.', 'Identity, values, family life, integration, and professional ethics — practical, contextual, grounded in Islamic principles, applied to contemporary contexts.']} primaryCta={{
      label: 'Explore Catalog',
      to: '#catalog'
    }} secondaryCta={{
      label: 'Request Info',
      to: '/en/contact'
    }} image="https://cdn.magicpatterns.com/uploads/1KJnu5Mw1PZbQZTNxTbn3x/CoreCourses3.jpg" imageAlt="Muslim woman studying with a laptop and notebook on a city balcony overlooking a tree-lined street" compact masked data-id="element-1687" data-mp="1d3s12xyr7xfb" />

      {/* What are Core Courses */}
      <section id="catalog" className="py-16 md:py-24 scroll-mt-24 bg-sage-50/60" data-id="element-1688" data-mp="jf409x5xzpen">
        <Container data-id="element-1689" data-mp="ngfr260jygof">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1690" data-mp="nfxo6qjdx47j">
            What are Core Courses?
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-1691" data-mp="23yg6kslq9sef">
            Core programs are the foundation of our adult direction. They focus
            on the most relevant areas of life: living confidently in non-Muslim
            societies, family and personal responsibilities, and ethical
            professional conduct.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-1692" data-mp="1pa39fsawm3ic">
            {courses.map((c, i) => <motion.div key={c.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px',
            ease: [0.22, 1, 0.36, 1]
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} whileHover={{
            y: -2
          }} className="rounded-2xl" data-id="element-1693" data-mp="1k9fdqtbqcyhs" data-mpi={i}>
                <Link to={c.to} className={`group block h-full rounded-2xl border border-ink-900/5 p-6 shadow-soft transition-shadow hover:shadow-card ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100', 'bg-[#EDF2F7]'][i % 4]}`} data-id="element-1694" data-mp="1tche7srv2wp1">
                  <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white border border-forest-700/10 text-forest-700" data-id="element-1695" data-mp="1ocuzfewdx8e4">
                    <c.icon size={18} strokeWidth={1.6} data-id="element-1696" data-mp="1an1akwkvqo4e" />
                  </span>
                  <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-1697" data-mp="1eo3cjl66lea2">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-1698" data-mp="10o2ba4m084u2">
                    {c.body}
                  </p>
                </Link>
              </motion.div>)}
          </div>

          <div className="mt-8 flex flex-col gap-2 rounded-2xl border border-sage-200 bg-white px-6 py-5 sm:flex-row sm:items-center" data-id="element-1699" data-mp="m0tumo9xf7ap">
            <span className="inline-flex w-fit whitespace-nowrap rounded-full bg-sage-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700" data-id="element-1700" data-mp="vyhs3lyx2swk">
              Coming soon
            </span>
            <p className="flex-1 text-center text-sm font-semibold text-ink-700" data-id="element-1701" data-mp="14yo0wujo74pe">
              The names of our confirmed teachers, educators, and Shariah Board
              members will be announced soon.
            </p>
          </div>
        </Container>
      </section>

      {/* Living in Non-Muslim countries */}
      <section id="living-in-non-muslim" className="py-16 md:py-24 scroll-mt-24 bg-white/60" data-id="element-1702" data-mp="19rh5nif52d1h">
        <Container data-id="element-1703" data-mp="1nq72cwy1qapb">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1704" data-mp="1nnkusxb5nv73">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1705" data-mp="1qs22604r15yz">
              <div data-id="element-1706" data-mp="1vqwnp9u3p8ms">
                <div className="flex items-center gap-3 mb-3" data-id="element-1707" data-mp="20ppzmxtqorku">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1708" data-mp="20o56ncn2pgp3">
                    8 Weeks
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1709" data-mp="29hry4qk8zxsh">
                    Core Course
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1710" data-mp="2jfo6xbppqgnf">
                  Living in non-Muslim countries: Essentials for Muslim Families
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1711" data-mp="p2py6wjnvs27">
                <p className="text-2xl font-display text-forest-700" data-id="element-1712" data-mp="p4j76ihqyodd">
                  €290{' '}
                  <span className="text-base text-ink-500 font-sans" data-id="element-1713" data-mp="u348kl8qje3e">
                    / course
                  </span>
                </p>
                <Link to="/waitlist" data-id="element-1714" data-mp="32kkksz162cop">
                  <Button variant="primary" data-id="element-1715" data-mp="32jjc317mi2a5">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1716" data-mp="2tk4ms536zxr8">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1717" data-mp="3qaevpb1xdr6b">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1718" data-mp="3m9l8kjwb4geh">
                  In 8 weeks, build a clear, confident plan for relocating and
                  thriving in non-Muslim countries without compromising your family's values.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1719" data-mp="25w4vn41yy5c4">
                  A practical roadmap for navigating legal, financial, and
                  practical essentials without overwhelm, while raising a strong
                  Muslim family and integrating with confidence.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1720" data-mp="3qzskx301r20u">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1721" data-mp="3r13lyv23xvo9">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1722" data-mp="nik2agxhvy52">
                    {['Families planning to relocate', 'New arrivals in non-Muslim countries', 'Parents seeking guidance', 'Professionals moving abroad'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1723" data-mp="iqai7hj29sap">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1724" data-mp="2qzgnbqmbzjbq">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1725" data-mp="35od3kayjk2nf">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1726" data-mp="30qbk3ox7e1bf">
                    {[{
                    icon: CalendarIcon,
                    text: '8-week cohort'
                  }, {
                    icon: UsersIcon,
                    text: 'Live weekly sessions'
                  }, {
                    icon: MapIcon,
                    text: 'Practical Q&A'
                  }, {
                    icon: UserCheckIcon,
                    text: 'Peer support group'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1727" data-mp="2mlrt918eeiro">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1728" data-mp="2ilr3wljrgn5u">
                          <f.icon size={14} strokeWidth={2} data-id="element-1729" data-mp="2drv5xj3hz0ao" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1730" data-mp="28umqvrdnbcdn">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1731" data-mp="1yytbm08jl4s6">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1732" data-mp="1ux7u21l2c2bn">
                    Where you'll be in 8 weeks
                  </h4>
                  <ul className="space-y-3" data-id="element-1733" data-mp="3p9o7bmca0gq0">
                    {['Clear relocation roadmap', 'Understanding of legal rights', 'Housing & finance basics', 'Schooling & community plan'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1734" data-mp="m82xo387gjd">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1735" data-mp="actba38pbzfh" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1736" data-mp="3muq3n7tm6hic">
                <div className="px-7 md:px-10 py-10" data-id="element-1737" data-mp="3ms508edo3tih">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1738" data-mp="y1m0zhyqdb1n">
                    Course Curriculum
                  </h4>

                  {/* 4. Subjects List */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1739" data-mp="2z3bqaqdmdywy">
                    {[{
                    num: '1',
                    title: 'Choosing Your Country',
                    desc: 'Community, cost, infrastructure'
                  }, {
                    num: '2',
                    title: 'Legal & Visa Essentials',
                    desc: 'Pathways, documents, family reunification'
                  }, {
                    num: '3',
                    title: 'Housing & Finances',
                    desc: 'Rental contracts, banking, halal investing, with referral to qualified scholars and certified institutions'
                  }, {
                    num: '4',
                    title: 'Career & Employment Rights',
                    desc: 'Job search, contracts, workplace religious rights, hiring, and fair termination'
                  }, {
                    num: '5',
                    title: 'Education & Academic Pathways',
                    desc: 'School systems, university admission, selection criteria, and enrollment processes'
                  }, {
                    num: '6',
                    title: 'Faith & Community Life',
                    desc: 'Mosques, halal food, Ramadan, hijab rights'
                  }, {
                    num: '7',
                    title: 'Education for Children',
                    desc: 'Academic institutions, Islamic identity, language'
                  }, {
                    num: '8',
                    title: 'Identity & Integration',
                    desc: 'Raising confident Muslim children in non-Muslim countries'
                  }].map(subject => <div key={subject.num} className="relative pl-6" data-id="element-1740" data-mp="3h7cac439jzft">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1741" data-mp="3h57ymzz4hw4z">
                          {subject.num}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-2 pt-1" data-id="element-1742" data-mp="8q7uc1mis2dg">
                          {subject.title}
                        </h5>
                        <p className="text-sm text-ink-600" data-id="element-1743" data-mp="3vvpa7c5h22fl">{subject.desc}</p>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1744" data-mp="2khl6vfczv1xv">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1745" data-mp="2fhfnsnksinre">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-4" data-id="element-1746" data-mp="25bxlyuorazr3">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1747" data-mp="2adwpq5oeryok">
                        €290
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1748" data-mp="2fadbwuc1s2z0">/ course</span>
                    </div>

                    <div className="space-y-3 mb-6" data-id="element-1749" data-mp="2k4rdjehaz6c4">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1750" data-mp="2o3i390jfvlp7">
                        <BookOpenIcon size={14} className="text-forest-700" data-id="element-1751" data-mp="2t2tj87bzen5q" />
                        <span data-id="element-1752" data-mp="2y2z516vklffw">8 weeks of live instruction</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1753" data-mp="331sg6sndwucw">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-1754" data-mp="32zg24qkqz454" />
                        <span data-id="element-1755" data-mp="13qj14cq8yde7">Small, dedicated cohorts</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-1756" data-mp="18okmx5f24qtd">
                        <CheckCircle2Icon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-1757" data-mp="10o4qecsb9u00" />
                        <span data-id="element-1758" data-mp="1alsp1dt1flng">Practical resources & templates</span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1759" data-mp="15l4wuot2oyrn">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1760" data-mp="1jlo59x1ga6uc">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Muslim Life & Integration */}
      <section id="muslim-life" className="relative py-16 md:py-24 overflow-hidden isolate scroll-mt-24 bg-[#F7EDE9]/60" data-id="element-1761" data-mp="22dgxvqclxq1j">
        <PatternDecor side="right" y="top" size="md" data-id="element-1762" data-mp="1y8r3y71ielj9" />
        <Container data-id="element-1763" data-mp="2h078xjg4ux0b">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1764" data-mp="2gznp1x6x9yjn">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1765" data-mp="2lvuvrs757e2n">
              <div data-id="element-1766" data-mp="2cw7vj9q5geb6">
                <div className="flex items-center gap-3 mb-3" data-id="element-1767" data-mp="2cvpr3ptc2ts6">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1768" data-mp="1y8ed7h9oixow">
                    8 Weeks
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1769" data-mp="1p8zp2ogziz7k">
                    Core Course
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1770" data-mp="1t777dnr3k8kv">
                  Muslim Life & Integration
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1771" data-mp="1k7k7x7hxhcug">
                <p className="text-2xl font-display text-forest-700" data-id="element-1772" data-mp="1zrenmcf1pkw8">
                  €200{' '}
                  <span className="text-base text-ink-500 font-sans" data-id="element-1773" data-mp="1ura92rxp708n">
                    / course
                  </span>
                </p>
                <Link to="/waitlist" data-id="element-1774" data-mp="1qondcfxwov66">
                  <Button variant="primary" data-id="element-1775" data-mp="1lr3ut3u6dw8f">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1776" data-mp="1gsalzopu2tel">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1777" data-mp="1c09aw64ej8q5">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1778" data-mp="130m4t70q9eqr">
                  In 8 weeks, learn to live calmly and confidently in non-Muslim
                  societies, maintaining your identity without conflict or
                  isolation.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1779" data-mp="34przzxj5mgr0">
                  Understand practical Shariah boundaries in everyday
                  environments and navigate social conduct, boundaries, and
                  respect with clarity.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1780" data-mp="3ipuhvjrgpa69">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1781" data-mp="3iorrdcy1x9qv">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1782" data-mp="2qzg9u464qs2i">
                    {['Muslim adults in the non-Muslim countries', 'Young professionals', 'Parents', 'Community members'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1783" data-mp="23811nuro9gjc">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1784" data-mp="1nqxqc67re9sa">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1785" data-mp="1nntf4ovvmds4">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1786" data-mp="vzrit4ppl9sz">
                    {[{
                    icon: CalendarIcon,
                    text: '8-week cohort'
                  }, {
                    icon: UsersIcon,
                    text: 'Interactive discussions'
                  }, {
                    icon: BookOpenIcon,
                    text: 'Applied fiqh scenarios'
                  }, {
                    icon: UserCheckIcon,
                    text: 'Expert facilitation'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1787" data-mp="4rhorzhcznid">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1788" data-mp="3vw80ckewvvip">
                          <f.icon size={14} strokeWidth={2} data-id="element-1789" data-mp="3rxqs1xjr8ezp" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1790" data-mp="3mxw0rsw2hne4">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1791" data-mp="3d4p02lc9uvkd">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1792" data-mp="389hwremwzsvz">
                    Where you'll be in 8 weeks
                  </h4>
                  <ul className="space-y-3" data-id="element-1793" data-mp="2z6gujp166lxl">
                    {['Confidence in daily interactions', 'Practical Shariah boundaries', 'Tools for respectful integration', 'Stronger sense of identity'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1794" data-mp="2fmi8cfawyb72">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1795" data-mp="389ciaia3ts6a" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1796" data-mp="3r7p1kwuqcifl">
                <div className="px-7 md:px-10 py-10" data-id="element-1797" data-mp="3vzgjyk3qfhun">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1798" data-mp="3w11ewatyl2t1">
                    Course Curriculum
                  </h4>

                  {/* 4. Subjects List */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1799" data-mp="1fl4tuhti3bsb">
                    {[{
                    num: '1',
                    title: 'Islamic Values & Daily Life',
                    desc: 'Core principles in practice'
                  }, {
                    num: '2',
                    title: 'Family Life & Responsibilities',
                    desc: 'Roles and relationships'
                  }, {
                    num: '3',
                    title: 'Fiqh for Everyday Life',
                    desc: 'Accessible and practical — no prior study required'
                  }, {
                    num: '4',
                    title: 'Adab & Social Conduct',
                    desc: 'Manners and interactions'
                  }, {
                    num: '5',
                    title: 'Understanding Western Culture',
                    desc: 'Cultural norms, societal values, and local context'
                  }, {
                    num: '6',
                    title: 'Living Islam in a Secular Society',
                    desc: 'Navigating modern challenges'
                  }, {
                    num: '7',
                    title: 'Discrimination & Legal Rights',
                    desc: 'Handling Islamophobia, workplace bias, and reporting mechanisms'
                  }, {
                    num: '8',
                    title: 'Legacy & Future Planning',
                    desc: 'Preserving faith across generations and long-term stability'
                  }].map(subject => <div key={subject.num} className="relative pl-6" data-id="element-1800" data-mp="27bav5v60p0zn">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1801" data-mp="23ah8t6883u8v">
                          {subject.num}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-2 pt-1" data-id="element-1802" data-mp="2r1we97vaqroc">
                          {subject.title}
                        </h5>
                        <p className="text-sm text-ink-600" data-id="element-1803" data-mp="2hbtmfazwvjrl">{subject.desc}</p>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1804" data-mp="39s4b588eklw3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1805" data-mp="351e8w3v9wz5m">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-4" data-id="element-1806" data-mp="134dr1eg6cmmp">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1807" data-mp="175pb74ga4fni">
                        €200
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1808" data-mp="tdsjv2mfq9s6">/ course</span>
                    </div>

                    <div className="space-y-3 mb-6" data-id="element-1809" data-mp="ybaxh6bedd6i">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1810" data-mp="jflgxnozds96">
                        <BookOpenIcon size={14} className="text-forest-700" data-id="element-1811" data-mp="oepm838sb5u8" />
                        <span data-id="element-1812" data-mp="ocle193urko5">8 weeks of live instruction</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1813" data-mp="ah9f0pinz81f">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-1814" data-mp="fejeyz3oj23i" />
                        <span data-id="element-1815" data-mp="24uivx2djhvtp">Small, dedicated cohorts</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-1816" data-mp="29r99nj1vgfby">
                        <CheckCircle2Icon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-1817" data-mp="2jvpp4bbfvuf8" />
                        <span data-id="element-1818" data-mp="2soj74gi9t7ws">Applied fiqh scenarios</span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1819" data-mp="2nxawxi949iik">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1820" data-mp="2nvq5i1i3mfox">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Family, Marriage & Personal Life */}
      <section id="family-marriage" className="py-16 md:py-24 scroll-mt-24 bg-sand-50/60" data-id="element-1821" data-mp="1h8ujdmx704ux">
        <Container data-id="element-1822" data-mp="1d8kgwk8lzwkt">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1823" data-mp="1d93ujor87yt1">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1824" data-mp="21qweqmx0tn1z">
              <div data-id="element-1825" data-mp="2bjlc6dvql0eb">
                <div className="flex items-center gap-3 mb-3" data-id="element-1826" data-mp="2bj24rr3ppuej">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1827" data-mp="2fkdmlagchbc6">
                    8 Weeks
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1828" data-mp="1ns68ohs84d1h">
                    Core Course
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1829" data-mp="1rwnpq9778c8k">
                  Family, Marriage & Personal Life
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1830" data-mp="338wxzyuoy48h">
                <p className="text-2xl font-display text-forest-700" data-id="element-1831" data-mp="3889r31c5o7ji">
                  €220{' '}
                  <span className="text-base text-ink-500 font-sans" data-id="element-1832" data-mp="3id9ceby3mkkg">
                    / course
                  </span>
                </p>
                <Link to="/waitlist" data-id="element-1833" data-mp="3de6hhkbu1uq7">
                  <Button variant="primary" data-id="element-1834" data-mp="38mwt0n20sxfw">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1835" data-mp="34m4izrlbqoik">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1836" data-mp="2zn9t65oxvzoy">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1837" data-mp="2po2dhbuqfsyo">
                  In 8 weeks, strengthen your relationships and navigate modern
                  family challenges with applied Islamic guidance and emotional
                  intelligence.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1838" data-mp="jvdlop19zdy4">
                  Support for adults in family and personal life, focusing on
                  strengthening relationships with respect, balance, and
                  responsibility.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1839" data-mp="1ixl4izrwcsxb">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1840" data-mp="1iwjv0zqj4ehp">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1841" data-mp="q9pc479d1n6c">
                    {['Married couples', 'Parents', 'Individuals seeking marriage', 'Family counselors'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1842" data-mp="v6pf29awy9t8">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1843" data-mp="3tkoo1wmhcnpr">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1844" data-mp="3thj0ilti1tw5">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1845" data-mp="3kjyu6ygs57eo">
                    {[{
                    icon: CalendarIcon,
                    text: '8-week cohort'
                  }, {
                    icon: UsersIcon,
                    text: 'Safe discussion space'
                  }, {
                    icon: BookOpenIcon,
                    text: 'Practical exercises'
                  }, {
                    icon: UserCheckIcon,
                    text: 'Scenario-based learning'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1846" data-mp="19ndrdp6d2n7f">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1847" data-mp="14pc8p5cukpwh">
                          <f.icon size={14} strokeWidth={2} data-id="element-1848" data-mp="zoo7jquwmsoj" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1849" data-mp="uqweuumfr2ja">{f.text}</span>
                      </div>)}
                  </div>
                  <p className="mt-5 rounded-xl border border-sage-200 bg-sage-50 px-4 py-3 text-sm leading-relaxed text-ink-700" data-id="element-1850" data-mp="lzdzun186srs">
                    To ensure the utmost comfort and privacy, sensitive and
                    scenario-based discussions are conducted in strictly
                    gender-separated groups, guided by professional moderators.
                  </p>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1851" data-mp="31jj1mtuvcyu">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1852" data-mp="2emk67z2ebmej">
                    Where you'll be in 8 weeks
                  </h4>
                  <ul className="space-y-3" data-id="element-1853" data-mp="2ir1n9qhdfllm">
                    {['Stronger family dynamics', 'Better communication skills', 'Understanding of rights & duties', 'Healthy boundaries'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1854" data-mp="1zypbye8qp9zc">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1855" data-mp="24vpfoii4a0na" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1856" data-mp="3iv1335v304a5">
                <div className="px-7 md:px-10 py-10" data-id="element-1857" data-mp="3exd1jjyvvd9a">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1858" data-mp="3smx0sft99sdu">
                    Course Curriculum
                  </h4>

                  {/* 4. Subjects List */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1859" data-mp="6f7670u3c7je">
                    {[{
                    num: '1',
                    title: 'Marriage & Family in Islam',
                    desc: 'Applied rights & responsibilities'
                  }, {
                    num: '2',
                    title: 'Islamic Family Law',
                    desc: 'Practical overview (non-legal)'
                  }, {
                    num: '3',
                    title: 'Civil Marriage & Divorce Laws',
                    desc: 'Legal registration, prenuptial agreements, and Western dissolution frameworks'
                  }, {
                    num: '4',
                    title: 'Inheritance & Asset Division',
                    desc: 'Estate planning, wealth distribution, and property rights under Western law'
                  }, {
                    num: '5',
                    title: 'Raising Children',
                    desc: 'Parenting, school, identity in non-Muslim countries'
                  }, {
                    num: '6',
                    title: 'Emotional Intelligence',
                    desc: 'Communication and relationships'
                  }, {
                    num: '7',
                    title: 'Conflict Resolution',
                    desc: 'Navigating modern family challenges calmly and constructively'
                  }, {
                    num: '8',
                    title: 'Personal Growth & Well-being',
                    desc: 'Maintaining individual and spiritual balance within the family'
                  }].map(subject => <div key={subject.num} className="relative pl-6" data-id="element-1860" data-mp="3q2j4yrqd3jmi">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1861" data-mp="3q4vh2o9fuzrn">
                          {subject.num}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-2 pt-1" data-id="element-1862" data-mp="3cal22yn8ujys">
                          {subject.title}
                        </h5>
                        <p className="text-sm text-ink-600" data-id="element-1863" data-mp="8u462xwba3h5">{subject.desc}</p>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1864" data-mp="l4db7t27ae74">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1865" data-mp="q3ghthxtwsh7">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-4" data-id="element-1866" data-mp="u3gzdbg85jsh">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1867" data-mp="z3v2qg6vs5k6">
                        €220
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1868" data-mp="141ekvr6p92he">/ course</span>
                    </div>

                    <div className="space-y-3 mb-6" data-id="element-1869" data-mp="18wtwsdl5cob6">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1870" data-mp="1ds974vjy422u">
                        <BookOpenIcon size={14} className="text-forest-700" data-id="element-1871" data-mp="1hvxhflzypbrs" />
                        <span data-id="element-1872" data-mp="1mtz6cnk2k5b6">8 weeks of live instruction</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1873" data-mp="284xglvckznfa">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-1874" data-mp="2870hxbj6p2zb" />
                        <span data-id="element-1875" data-mp="235umv721x3mm">Small, dedicated cohorts</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-1876" data-mp="2i2v4ghqj3i7d">
                        <CheckCircle2Icon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-1877" data-mp="2d5bnha2g4rbq" />
                        <span data-id="element-1878" data-mp="1kg4q4eeqa7s2">Safe discussion space</span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1879" data-mp="1z5khnwm32h8t">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1880" data-mp="1udsm17o2llbt">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Professional & Business Programs */}
      <section id="professional-business" className="relative py-16 md:py-24 overflow-hidden isolate scroll-mt-24 bg-white/60" data-id="element-1881" data-mp="3i48n8rez53rx">
        <PatternDecor side="left" y="middle" size="md" data-id="element-1882" data-mp="33cg5y9vmdfnk" />
        <Container data-id="element-1883" data-mp="38e5kw0lyqdgx">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-1884" data-mp="38dngggp5csxx">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1885" data-mp="4wnapmipwudc">
              <div data-id="element-1886" data-mp="3n1ibw4ujhbep">
                <div className="flex items-center gap-3 mb-3" data-id="element-1887" data-mp="3n4ct7ni9ag3w">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-1888" data-mp="3r2u32et2a4p0">
                    8 Weeks
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-1889" data-mp="2fphujqmcwxwi">
                    Core Course
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-1890" data-mp="tuksxiau3r3a">
                  Professional & Business Programs
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-1891" data-mp="g50wsvbrds2y">
                <p className="text-2xl font-display text-forest-700" data-id="element-1892" data-mp="l3c694y560kn">
                  €320{' '}
                  <span className="text-base text-ink-500 font-sans" data-id="element-1893" data-mp="18swqdr6173vi">
                    / course
                  </span>
                </p>
                <Link to="/waitlist" data-id="element-1894" data-mp="1crcjmcsph8hd">
                  <Button variant="primary" data-id="element-1895" data-mp="yu783xa83heq">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-1896" data-mp="13rh2zsgdsl9y">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-1897" data-mp="3jj529uivapvx">
                {/* 1. Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-1898" data-mp="3gyyv7dyyijb5">
                  In 8 weeks, master ethical decision-making, cross-cultural
                  communication, and responsible business practices for the
                  modern workplace.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1899" data-mp="3l0k7oy7kwvkt">
                  Designed for entrepreneurs, specialists, and managers focusing
                  on ethics, trust, and responsibility in professional life.
                </p>

                {/* 3a. Who it's for (Chips) */}
                <div className="mt-10" data-id="element-1900" data-mp="371u8nd178ltt">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1901" data-mp="372vi5d2kh09f">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1902" data-mp="2ek81qayim9eo">
                    {['Entrepreneurs', 'Managers & leaders', 'Specialists', 'Business owners'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-1903" data-mp="ihljesj621tz">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* 3b. Format (Icon row) */}
                <div className="mt-10" data-id="element-1904" data-mp="3vo51sampsy0z">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-1905" data-mp="3vp6j2wubqgr5">
                    Format
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1906" data-mp="8k71splib703">
                    {[{
                    icon: CalendarIcon,
                    text: '8-week cohort'
                  }, {
                    icon: BookOpenIcon,
                    text: 'Case studies'
                  }, {
                    icon: UsersIcon,
                    text: 'Professional networking'
                  }, {
                    icon: UserCheckIcon,
                    text: 'Negotiation practice'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-1907" data-mp="2lq59u9fb29u8">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-1908" data-mp="2lo11nfadioo5">
                          <f.icon size={14} strokeWidth={2} data-id="element-1909" data-mp="2coxi6a7ao794" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-1910" data-mp="27re1l3n4jie0">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* 3c. Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-1911" data-mp="60aqkd9bo1sx">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-1912" data-mp="2ytho37ph02qw">
                    Where you'll be in 8 weeks
                  </h4>
                  <ul className="space-y-3" data-id="element-1913" data-mp="33s19n705jciq">
                    {['Ethical business framework', 'Stronger negotiation skills', 'Cross-cultural competence', 'Practical finance basics'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-1914" data-mp="2g1f2rj1vvyab">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-1915" data-mp="2744v07o9yra5" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Timeline & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-1916" data-mp="1ijrp2in21i5r">
                <div className="px-7 md:px-10 py-10" data-id="element-1917" data-mp="1djngdese1rq3">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-1918" data-mp="1sfv06dzvwr4u">
                    Course Curriculum
                  </h4>

                  {/* 4. Subjects List */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-1919" data-mp="21g9oznc24od3">
                    {[{
                    num: '1',
                    title: 'Islamic Ethics in Professional Life',
                    desc: 'Ethics at work and in business'
                  }, {
                    num: '2',
                    title: 'Business Registration & Structure',
                    desc: 'Legal frameworks, company incorporation, and Western compliance'
                  }, {
                    num: '3',
                    title: 'Taxation & Financial Regulation',
                    desc: 'Tax systems, corporate reporting, and lawful optimization'
                  }, {
                    num: '4',
                    title: 'Islamic Finance (Practical Basics)',
                    desc: 'Understanding, not certification'
                  }, {
                    num: '5',
                    title: 'Business in Diverse Societies',
                    desc: 'Negotiations, partnerships, contracts'
                  }, {
                    num: '6',
                    title: 'Cross-Cultural Communication',
                    desc: 'Effective communication across cultures'
                  }, {
                    num: '7',
                    title: 'Negotiation & Conflict Resolution',
                    desc: 'Practical tools for conflict'
                  }, {
                    num: '8',
                    title: 'Leadership & Growth',
                    desc: 'Scaling businesses with high ethical and professional standards'
                  }].map(subject => <div key={subject.num} className="relative pl-6" data-id="element-1920" data-mp="3pz54bvkc6dau">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-1921" data-mp="qkteft11glwq">
                          {subject.num}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-2 pt-1" data-id="element-1922" data-mp="cmv0goqmqrhr">
                          {subject.title}
                        </h5>
                        <p className="text-sm text-ink-600" data-id="element-1923" data-mp="329aulmvbd4ut">{subject.desc}</p>
                      </div>)}
                  </div>

                  {/* 5. Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-1924" data-mp="11bsesorylhpm">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-1925" data-mp="16663k8c48qlb">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-4" data-id="element-1926" data-mp="wb4fzgxhnwku">
                      <span className="font-display text-3xl text-ink-900" data-id="element-1927" data-mp="1jzdx45jrbw5n">
                        €320
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-1928" data-mp="1oyh1rovts0d3">/ course</span>
                    </div>

                    <div className="space-y-3 mb-6" data-id="element-1929" data-mp="1a9ksxply2jbf">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1930" data-mp="1f8p24g8vch1r">
                        <BookOpenIcon size={14} className="text-forest-700" data-id="element-1931" data-mp="22yrpij1u8yu1" />
                        <span data-id="element-1932" data-mp="22wov7mtnkjji">8 weeks of live instruction</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-1933" data-mp="27rkwzeooyl9t">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-1934" data-mp="3sec90mc9gkyc" />
                        <span data-id="element-1935" data-mp="3nfsmajpuf54x">Small, dedicated cohorts</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-1936" data-mp="60ls100cuixc">
                        <CheckCircle2Icon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-1937" data-mp="11hho61p2f5f" />
                        <span data-id="element-1938" data-mp="39ivticq5zdle">Professional networking</span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-1939" data-mp="3ihhc27vmysm6">
                      <Button variant="primary" className="w-full justify-center" data-id="element-1940" data-mp="3ifmoelszcmee">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* How courses work */}
      <section className="py-16 md:py-24 bg-[#EDF2F7]/60" data-id="element-1941" data-mp="18a4zrwabbh5e">
        <Container data-id="element-1942" data-mp="13clmaylfv8ei">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1943" data-mp="13c3hveomhnvi">
            How Core Courses work
          </h2>
          <p className="mt-5 text-ink-700 max-w-3xl leading-relaxed" data-id="element-1944" data-mp="tfgvumkk8qrw">
            Short, focused, and applied — designed for adults who want clarity
            without academic overhead.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-1945" data-mp="1m3cpfieu22kg">
            {courseFeatures.map((f, i) => {
            const tones = ['bg-white', 'bg-[#EDF2F7]', 'bg-[#F7EDE9]'];
            return <motion.div key={f.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.06
            }} className={`rounded-2xl ${tones[i % tones.length]} border border-ink-900/5 p-6 shadow-soft`} data-id="element-1946" data-mp="1cemh7ot79id0" data-mpi={i}>
                  <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white text-forest-700" data-id="element-1947" data-mp="2r92te94ou0rk">
                    <f.icon size={18} strokeWidth={1.6} data-id="element-1948" data-mp="30bu6edd7p4lb" />
                  </span>
                  <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-1949" data-mp="309hi7igv1xzt">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-1950" data-mp="3a1dqfmcnv4hr">
                    {f.body}
                  </p>
                </motion.div>;
          })}
          </div>
        </Container>
      </section>

      {/* Access & Scholarships */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-1951" data-mp="3jxqhsdkqh69c">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-1952" data-mp="2u9l5doxw65zs" />
        <Container data-id="element-1953" data-mp="2pdnp1immozn0">
          <h3 className="font-display text-lg text-ink-900" data-id="element-1954" data-mp="2pe5th2jg2k60">
            Access & Scholarships
          </h3>
          <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-1955" data-mp="342tliyu30br7">
            Some cohorts may include limited scholarship places through
            community partnerships to ensure our programs remain accessible to
            motivated learners.
          </p>
        </Container>
      </section>

      {/* Educational disclaimer */}
      <section className="py-10 md:py-14 bg-white/60" data-id="element-1956" data-mp="3n0cww940i6gs">
        <Container data-id="element-1957" data-mp="3mzusuqb3ynyb">
          <aside aria-labelledby="core-courses-disclaimer" className="rounded-2xl border border-[#E0BFB3]/60 bg-[#F7EDE9] px-6 py-5 md:px-8 md:py-6" data-id="element-1958" data-mp="3mya9lwu8urfr">
            <h2 id="core-courses-disclaimer" className="font-display text-lg text-ink-900" data-id="element-1959" data-mp="1smvfz4y447uq">
              Disclaimer
            </h2>
            <p className="mt-3 max-w-5xl text-sm leading-relaxed text-ink-700" data-id="element-1960" data-mp="369gqjvqbb2d">
              Our programs provide general, applied educational content.
            </p>
            <ul className="mt-4 max-w-5xl list-disc space-y-3 pl-5 text-sm leading-relaxed text-ink-700 marker:text-ink-900" data-id="element-1961" data-mp="3pl2idwed6jau">
              <li className="pl-1" data-id="element-1962" data-mp="3uiubgtqqwbgm">
                <strong className="text-ink-900" data-id="element-1963" data-mp="3uhsyuku2zgws">
                  Personal &amp; Family Matters:
                </strong>{' '}
                For specific legal or personal rulings (<em data-id="element-1964" data-mp="3kmhjc0aum5nz">fatwas</em>)
                regarding marriage (<em data-id="element-1965" data-mp="36lx0x58uibv0">nikah</em>), divorce, or inheritance,
                please consult a qualified Islamic scholar or a recognized
                Sharia council. We are glad to help facilitate a connection to
                trusted scholarly authorities upon request.
              </li>
              <li className="pl-1" data-id="element-1966" data-mp="14sr1fz9jha7b">
                <strong className="text-ink-900" data-id="element-1967" data-mp="19twtduvdktfs">
                  Business &amp; Finance:
                </strong>{' '}
                Professional, financial, and business-related content is
                strictly educational and practical in nature and does not
                constitute regulated legal, tax, or financial advice.
              </li>
            </ul>
          </aside>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-1968" data-mp="2rwquq9p01z5p">
        <Container data-id="element-1969" data-mp="2my74hx3di17j">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-1970" data-mp="2myp8xh06vlqj">
            <div className="max-w-xl" data-id="element-1971" data-mp="2i65u6gprmkk5">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1972" data-mp="2i5e05abtguvz">
                Start with a Core Course
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-1973" data-mp="297k6y89cmhd1">
                Choose a track, request details, and join the next cohort.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-1974" data-mp="1z8csfhqvogoc">
              <Link to="/en/waitlist" data-id="element-1975" data-mp="1z9nund4odgdc">
                <Button variant="primary" data-id="element-1976" data-mp="1zaparw0jst1x">Join</Button>
              </Link>
              <Link to="/en/contact" data-id="element-1977" data-mp="eq2blvsuv2pt">
                <Button variant="secondary" data-id="element-1978" data-mp="jp546grnfggw">Request Info</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}